import {useCallback, useState, type FormEvent} from 'react';
import {Banner, Button, FormLayout, Link, Text, TextField} from '@shopify/polaris';
import type {PagePropsBase} from '@slj/ui/document';

import {AuthLayout} from './auth-layout';

export interface SignInProps extends PagePropsBase {
  redirectTo: string;
  appName?: string;
  notice?: string;
}

/**
 * Posts to Better Auth's own endpoint via fetch (not a native form POST) so its JSON
 * error surfaces inline instead of dumping the user on a JSON page. Hydrated; the
 * SSR'd markup is identical, so the form is usable the moment app.js lands.
 */
export function SignInPage({redirectTo, appName, notice}: SignInProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(notice);
  const [loading, setLoading] = useState(false);

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setError(undefined);
      setLoading(true);
      try {
        const res = await fetch('/api/auth/sign-in/email', {
          method: 'POST',
          headers: {'content-type': 'application/json'},
          credentials: 'include',
          body: JSON.stringify({email: email.trim(), password}),
        });
        if (res.ok) {
          window.location.href = redirectTo;
          return;
        }
        const data = (await res.json().catch(() => ({}))) as {message?: string};
        setError(data.message || 'That email and password combination did not work.');
      } catch {
        setError('Could not reach the server. Try again.');
      }
      setLoading(false);
    },
    [email, password, redirectTo],
  );

  return (
    <AuthLayout title="Sign in">
      {appName ? <Text as="p" tone="subdued">{`to continue to ${appName}`}</Text> : null}

      {error ? (
        <Banner tone="critical" onDismiss={() => setError(undefined)}>
          <p>{error}</p>
        </Banner>
      ) : null}

      <form onSubmit={onSubmit}>
        <FormLayout>
          <TextField
            label="Email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={setEmail}
            requiredIndicator
            autoFocus
          />
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
            requiredIndicator
          />
          <Button submit variant="primary" fullWidth loading={loading}>
            Sign in
          </Button>
        </FormLayout>
      </form>

      <Text as="p" tone="subdued" alignment="center">
        <Link url="/forgot">Forgot your password?</Link>
      </Text>
    </AuthLayout>
  );
}
